import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { createCheck } from "../actions/representationActions";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { useNavigate } from "react-router-dom";
import { UploadInputInterface } from "./ui/UploadInputInterface";
import toast from "react-hot-toast";
import { getFormErrors } from "../utils/formErrors";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { CHECK_LABELS, validateCheck } from "../utils/recordValidation";
import { formatNumber, toLatinDigits } from "../utils/numberFormat";
import {
  BadgeCheck,
  Banknote,
  Building2,
  CalendarDays,
  FileUp,
  Hash,
  Loader2,
  User,
  UserRound,
  X,
} from "lucide-react";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const Field = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  required,
  icon: Icon,
  inputMode,
  error,
}) => {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-slate-700 text-right"
      >
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon size={18} />
          </span>
        ) : null}

        <input
          id={id}
          name={name}
          type={type}
          inputMode={inputMode}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          dir={inputMode === "numeric" ? "ltr" : "rtl"}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cx(
            "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
            "placeholder:text-slate-400 shadow-sm",
            "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
            Icon ? "pr-10" : "pr-3"
          )}
        />
      </div>
      {error && <p id={`${id}-error`} className="text-sm text-rose-700">{error}</p>}
    </div>
  );
};

const RepresentationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const pending = useRef(false);

  const [formData, setFormData] = useState({
    check_code: "",
    issuer: "",
    value: "",
    issued_for: "",
    bank: "",
    date: "", // Jalali string "YYYY-MM-DD"
    is_paid: false,
    document: null,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : ["check_code", "value"].includes(name) ? toLatinDigits(value) : value,
    }));
    setFieldErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const handleDateChange = (val) => {
    // With persian calendar, format() returns Jalali date (e.g., 1404-09-22) which is often desired in Iranian systems.
    setFormData((prev) => ({
      ...prev,
      date: val ? toLatinDigits(val.format("YYYY-MM-DD")) : "",
    }));
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0] || null;
    setFormData((prev) => ({ ...prev, document: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pending.current) return;
    const { lines: problems, fields: problemFields } = validateCheck(formData);
    if (problems.length) {
      setFieldErrors(problemFields);
      setErrorMsg("لطفاً موارد مشخص‌شده را اصلاح کنید.");
      toastErrors(problems);
      return;
    }
    pending.current = true;
    setErrorMsg("");
    setFieldErrors({});
    setSubmitting(true);

    try {
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "document") data.append(key, formData[key]);
      });

      if (formData.document instanceof File) {
        data.append("document", formData.document);
      }

      await dispatch(createCheck(data));
      toast.success("چک با موفقیت ایجاد شد.");
      navigate("/checks");
    } catch (err) {
      const errors = getFormErrors(err, Object.keys(formData), "چک ثبت نشد. لطفاً دوباره تلاش کنید.");
      setErrorMsg(errors.general);
      setFieldErrors(errors.fields);
      toastApiError(err, CHECK_LABELS, "چک ثبت نشد. لطفاً دوباره تلاش کنید.");
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-24 px-3 pb-8 sm:px-6 font-vazir">
      <form onSubmit={handleSubmit} noValidate aria-busy={submitting} dir="rtl" className="max-w-3xl mx-auto">
        <fieldset disabled={submitting} className="min-w-0">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden">
          {/* Header */}
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-row-reverse items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate("/checks")}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
              >
                <X size={18} />
                بازگشت
              </button>

              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <BadgeCheck size={22} />
                </div>
                <div className="text-right">
                  <h2 className="text-lg text-right sm:text-xl font-extrabold text-slate-800">
                    افزودن چک
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    اطلاعات چک را وارد کنید و در صورت نیاز فایل را پیوست کنید.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 py-6">
            {errorMsg ? (
              <div role="alert" className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
                {errorMsg}
                {fieldErrors.date && <p>تاریخ: {fieldErrors.date}</p>}
                {fieldErrors.document && <p>فایل پیوست: {fieldErrors.document}</p>}
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                id="check_code"
                name="check_code"
                error={fieldErrors.check_code}
                label="کد چک"
                placeholder="مثلاً 123456"
                value={formData.check_code}
                onChange={handleChange}
                required
                icon={Hash}
                inputMode="numeric"
              />

              <Field
                id="bank"
                name="bank"
                error={fieldErrors.bank}
                label="بانک"
                placeholder="مثلاً ملی، ملت، پاسارگاد..."
                value={formData.bank}
                onChange={handleChange}
                required
                icon={Building2}
              />

              <Field
                id="issuer"
                name="issuer"
                error={fieldErrors.issuer}
                label="صادر کننده"
                placeholder="نام صادر کننده چک"
                value={formData.issuer}
                onChange={handleChange}
                required
                icon={User}
              />

              <Field
                id="issued_for"
                name="issued_for"
                error={fieldErrors.issued_for}
                label="در وجه"
                placeholder="نام صاحب امتیاز"
                value={formData.issued_for}
                onChange={handleChange}
                required
                icon={UserRound}
              />

              <Field
                id="value"
                name="value"
                error={fieldErrors.value}
                label="مبلغ"
                placeholder="مثلاً 25000000"
                value={formData.value}
                onChange={handleChange}
                required
                type="text"
                icon={Banknote}
                inputMode="numeric"
              />
              {formData.value && <p className="text-sm tabular-nums text-slate-500 md:col-span-2">مبلغ واردشده: <bdi dir="ltr">{formatNumber(formData.value, { maximumFractionDigits: 0 })}</bdi></p>}

              {/* Date */}
              <div className="space-y-1.5">
                <label
                  htmlFor="date"
                  className="block text-sm font-medium text-slate-700 text-right"
                >
                  تاریخ چک <span className="text-rose-500">*</span>
                </label>

                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    id="date"
                    name="date"
                    calendar={persian}
                    locale={persian_fa}
                    value={formData.date}
                    onChange={handleDateChange}
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>
              </div>
            </div>

            {/* Paid toggle + file */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Switch */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      وضعیت چک
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      در صورت پاس شدن، فعال کنید.
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="is_paid"
                      name="is_paid"
                      checked={!!formData.is_paid}
                      onChange={handleChange}
                      className="sr-only peer"
                    />

                    {/* Track + knob via after: */}
                    <span
                      className="
      relative w-11 h-6 rounded-full bg-slate-200 transition-colors
      peer-checked:bg-primary
      after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
      after:rounded-full after:bg-white after:shadow after:transition-transform
      peer-checked:after:-translate-x-5
    "
                    />

                    <span className="mr-3 text-sm font-medium text-slate-700">
                      پاس شده
                    </span>
                  </label>
                </div>
              </div>

              {/* File */}
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      فایل پیوست
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      تصویر یا PDF چک (اختیاری)
                    </div>
                  </div>
                  <div className="text-slate-400 mt-0.5">
                    <FileUp size={18} />
                  </div>
                </div>

                <div className="mt-3">
                  <UploadInputInterface
                    file={formData.document}
                    onFileChange={handleFileChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 sm:px-7 py-4 border-t border-slate-200 bg-white">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => {
                  setErrorMsg("");
                  setFormData({
                    check_code: "",
                    issuer: "",
                    value: "",
                    issued_for: "",
                    bank: "",
                    date: "",
                    is_paid: false,
                    document: null,
                  });
                }}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
                disabled={submitting}
              >
                پاک کردن فرم
              </button>

              <button
                type="submit"
                disabled={submitting}
                className={cx(
                  "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white",
                  "bg-primary hover:opacity-95 active:opacity-90 transition shadow-sm",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    در حال ثبت...
                  </>
                ) : (
                  "ثبت"
                )}
              </button>
            </div>
          </div>
        </div>
        </fieldset>
      </form>
    </div>
  );
};

export default RepresentationForm;
